"use client";

import { Badge, Button, Col, Flex, Row } from "antd";
import styles from "./booking.module.css";
import dayjs from "dayjs";
import { useMemo, useState, useEffect } from "react";
import { getAvailableSteps, getNextStep } from "./stepFlow";
import {
  hasAnyActiveSeasonPasses,
  hasSeasonPassesForVenueDates,
  areAllSeasonPassesClosed,
} from "@/lib/seasonPassHelpers";
import SeasonPassOfferCard from "./SeasonPassOfferCard";
import { trackSelectEventDate } from "@/lib/analytics";
import { formatEventTime } from "@/lib/formatDateTime";

/* =====================================================
   🔥 STATUS RESOLVERS (Single Source of Truth)
===================================================== */

// ✅ DATE STATUS
const getDateStatus = (date, eventData) => {
  const now = dayjs();

  const allEvents = eventData?.events || [];
  let isPastDate = false;

  if (allEvents.length > 0) {
    // If all events for this date have ended, then the date is past.
    isPastDate = allEvents.every((e) => {
      const targetEndDate = e?.end_date || date;
      const targetEndTime = e?.end_time || "23:59:59";
      return dayjs(
        `${targetEndDate} ${targetEndTime}`,
        "YYYY-MM-DD HH:mm:ss",
      ).isBefore(now);
    });
  } else {
    isPastDate = dayjs(date).endOf("day").isBefore(now);
  }

  const allTickets = eventData?.events?.flatMap((e) => e?.tickets || []) || [];

  const isSoldOut =
    eventData?.sold_out ||
    (allTickets.length > 0 &&
      allTickets.every(
        (t) =>
          t?.is_sold_out === true ||
          Number(t?.available ?? t?.available_quantity ?? 0) === 0,
      ));

  if (isPastDate) return "closed";
  if (isSoldOut) return "sold_out";

  return "available";
};

// ✅ TIME SLOT STATUS
const getTimeStatus = (date, item) => {
  const now = dayjs();

  const targetEndDate = item?.end_date || date;
  const targetEndTime = item?.end_time || "23:59:59";

  const isExpired = dayjs(
    `${targetEndDate} ${targetEndTime}`,
    "YYYY-MM-DD HH:mm:ss",
  ).isBefore(now);

  if (isExpired) return "closed";
  if (item?.sold_out) return "sold_out";

  return "available";
};

// ✅ UI CONFIG
const getRibbon = (status) => {
  switch (status) {
    case "sold_out":
      return { text: "Sold Out", color: "#EE4B2B" };
    case "closed":
      return { text: "Completed", color: "#5cedf3" };
    default:
      return null;
  }
};

/* =====================================================
   COMPONENT
===================================================== */

export default function DateTimeStep({
  dates,
  data,
  setActiveStep,
  setTickets,
  orderDetails,
  setOrderDetails,
  needsVenueSelection = false,
}) {
  const [event, setEvent] = useState(() =>
    orderDetails?.date && dates ? dates[orderDetails?.date] : null,
  );

  useEffect(() => {
    if (!event || orderDetails?.time) return;

    if (event?.events?.length === 1) {
      const ev = event.events[0];
      const status = getTimeStatus(orderDetails?.date, ev);
      if (status === "available") {
        commitSelection(orderDetails?.date, ev);
      }
    }
  }, [event, orderDetails?.time, orderDetails?.date]);

  // Same rule as Venue: show Season Pass entry FIRST when passes exist.
  // Skip it here when a Venue step precedes this one — it already showed
  // there, so repeating it on Date & Time would be a duplicate.
  const showSeasonPassOffer = useMemo(() => {
    if (needsVenueSelection) return false;
    if (hasSeasonPassesForVenueDates(data, dates)) return true;
    return hasAnyActiveSeasonPasses(data);
  }, [data, dates, needsVenueSelection]);
  const seasonPassesClosed = useMemo(
    () => areAllSeasonPassesClosed(data),
    [data],
  );

  const advanceFromSelection = (ev, { bookingPath = "individual" } = {}) => {
    const evIsZone = Boolean(ev?.is_zone_config);
    const evIsSeating = Boolean(ev?.seating_configuration);

    const flowList = getAvailableSteps({
      isGroup: true,
      needsVenueSelection: !!orderDetails?.venue,
      isMultiDate: true,
      isZone: evIsZone,
      isSitting: evIsSeating,
      haveConfiguredSeats: evIsSeating,
      bookingPath,
      seasonPassStepActive: false,
      // Recurring show: pick the day + time slot next.
      slotShowId: ev?.show_type === "recurring" ? ev?.child_event_id : null,
    });

    // An open-daily season goes to its date & slot picker
    const nextStep =
      ev?.show_type === "recurring"
        ? "slot"
        : (getNextStep("datetime", flowList) ?? "tickets");
    setActiveStep(nextStep);
  };

  const commitSelection = (date, ev) => {
    trackSelectEventDate({
      event_id: ev?.child_event_id || data?.id,
      event_name: data?.name,
      event_date: date,
      city: data?.address?.city || data?.venue?.city,
      venue: data?.venue?.venue_name || data?.venue?.name,
    });

    setTickets(ev?.tickets);

    setOrderDetails((prev) => ({
      ...prev,
      parent_event_id: data?.id,
      date,
      time: ev?.start_time,
      end_date: ev?.end_date,
      end_time: ev?.end_time,
      timezone: ev?.timezone,
      ticket: null,
      zone: null,
      layout_type: ev?.layout_type,
      is_zone_config: ev?.is_zone_config,
      event_id: ev?.child_event_id,
      seating_configuration: ev?.seating_configuration,
      seating_layout_data: null,
      zone_seats: [],
      seating_layout_event_id: null,
      seating_layout_zone_id: null,
      session_id: null,
      session_created_at: null,
      booking_path: "individual",
      season_pass_step: false,
      is_season_pass: false,
      _venue_dates: prev?._venue_dates || dates || null,
    }));

    advanceFromSelection(ev, { bookingPath: "individual" });
  };

  // Open-daily shows run over a season: the customer picks the day and the
  // time slot in the next step, so they get their own card instead of
  // showing up as a date (their first day) in the date grid.
  const { seasons, oneTimeDates } = useMemo(() => {
    const seen = new Map();
    const rest = {};
    Object.entries(dates || {}).forEach(([date, eventData]) => {
      const evs = Array.isArray(eventData) ? eventData : eventData?.events || [];
      const single = evs.filter((ev) => ev?.show_type !== "recurring");
      evs
        .filter((ev) => ev?.show_type === "recurring")
        .forEach((ev) => {
          if (!seen.has(ev.child_event_id)) seen.set(ev.child_event_id, { date, ev });
        });
      if (single.length) {
        rest[date] = Array.isArray(eventData)
          ? single
          : { ...eventData, events: single };
      }
    });
    return { seasons: [...seen.values()], oneTimeDates: rest };
  }, [dates]);

  const openSeasonPasses = () => {
    setOrderDetails((prev) => ({
      ...prev,
      ticket: null,
      booking_path: null,
      season_pass_step: true,
      is_season_pass: false,
      // Same entry pattern as Venue — list passes for this screen without a date pick.
      season_pass_entry: "datetime_screen",
      _venue_dates: prev?._venue_dates || dates || null,
    }));
    setActiveStep("season_passes");
  };

  return (
    <div className={styles.dateTimeSelection}>
      <Flex gap={42} vertical>
        {/* ===================== SEASON PASS (FIRST) ===================== */}
        {showSeasonPassOffer && (
          <button
            type="button"
            onClick={
              seasonPassesClosed || data?.status === "completed"
                ? undefined
                : openSeasonPasses
            }
            disabled={seasonPassesClosed || data?.status === "completed"}
            aria-disabled={seasonPassesClosed || data?.status === "completed"}
            className={`w-full text-left border-0 bg-transparent p-0 ${seasonPassesClosed || data?.status === "completed"
              ? "cursor-not-allowed"
              : "cursor-pointer"
              }`}
          >
            <SeasonPassOfferCard
              title="Season Pass Available"
              description="Cover multiple dates with one pass"
              labelsOnly={false}
              closed={seasonPassesClosed || data?.status === "completed"}
            />
          </button>
        )}

        {/* ===================== OPEN DAILY ===================== */}
        {seasons.length > 0 && (
          <div>
            <h3 className={styles.sectionTitle2}>Open daily</h3>
            <Flex vertical gap={12}>
              {seasons.map(({ date, ev }) => {
                const ended = dayjs(
                  `${ev?.end_date || date} ${ev?.end_time || "23:59:59"}`,
                ).isBefore(dayjs());
                return (
                  <button
                    key={ev.child_event_id}
                    type="button"
                    disabled={ended}
                    onClick={() => !ended && commitSelection(date, ev)}
                    className={styles.seasonCard}
                  >
                    <strong>
                      {dayjs(date).format("D MMM")} –{" "}
                      {dayjs(ev?.end_date || date).format("D MMM YYYY")}
                    </strong>
                    <span>
                      {ended
                        ? "This season has ended."
                        : "Pick your date and time slot next."}
                    </span>
                  </button>
                );
              })}
            </Flex>
          </div>
        )}

        {/* ===================== DATE ===================== */}
        {Object.keys(oneTimeDates).length > 0 && (
        <div>
          <h3 className={styles.sectionTitle2}>
            {seasons.length ? "Or pick a show date" : "Select date"}
          </h3>

          <Row gutter={[16, 16]}>
            {Object.entries(oneTimeDates).map(([date, eventData]) => {
              const status = getDateStatus(date, eventData);
              const ribbon = getRibbon(status);
              const disabled = status !== "available";

              const dateButton = (
                <Button
                  block
                  size="large"
                  disabled={disabled}
                  style={{ height: 54 }}
                  className={`${orderDetails?.date === date ? "border-2! border-black!" : ""} justify-start!`}
                  onClick={() => {
                    if (disabled) return;

                    setEvent(
                      Array.isArray(eventData) ? { events: eventData } : eventData,
                    );

                    setOrderDetails((prev) => ({
                      ...prev,
                      date,
                      time: null,
                      ticket: null,
                      zone: null,
                      event_id: null,
                      seating_layout_data: null,
                      zone_seats: [],
                      seating_layout_event_id: null,
                      seating_layout_zone_id: null,
                      session_id: null,
                      session_created_at: null,
                      booking_path: null,
                      season_pass_step: false,
                      is_season_pass: false,
                      _venue_dates: prev?._venue_dates || dates || null,
                    }));
                  }}
                >
                  {dayjs(date).format("D MMM")}
                </Button>
              );

              return (
                <Col xs={12} sm={8} md={6} key={date}>
                  {ribbon ? (
                    <Badge.Ribbon text={ribbon.text} color={ribbon.color}>
                      {dateButton}
                    </Badge.Ribbon>
                  ) : (
                    dateButton
                  )}
                </Col>
              );
            })}
          </Row>
        </div>
        )}

        {/* ===================== TIME ===================== */}
        {event?.events?.length > 1 && (
          <div>
            <h3 className={styles.sectionTitle2}>Select time</h3>

            <Row gutter={[16, 16]}>
              {event.events.map((item, index) => {
                const status = getTimeStatus(orderDetails.date, item);
                const ribbon = getRibbon(status);
                const disabled = status !== "available";

                const timeButton = (
                  <Button
                    block
                    size="large"
                    disabled={disabled}
                    style={{ height: 54 }}
                    className={`${orderDetails?.time === item?.start_time ? "border-2! border-black!" : ""} justify-start!`}
                    onClick={() => {
                      if (disabled) return;
                      commitSelection(orderDetails.date, item);
                    }}
                  >
                    {dayjs(item?.start_time, "HH:mm:ss").format("hh:mm A")}
                  </Button>
                );

                return (
                  <Col xs={7} key={index}>
                    {ribbon ? (
                      <Badge.Ribbon text={ribbon.text} color={ribbon.color}>
                        {timeButton}
                      </Badge.Ribbon>
                    ) : (
                      timeButton
                    )}
                  </Col>
                );
              })}
            </Row>
          </div>
        )}
      </Flex>
    </div>
  );
}
